import clsx from 'clsx';
import { type CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BiSolidChevronDown, BiX } from 'react-icons/bi';

import { useClickOutside } from '@/hooks/useClickOutside';

import s from './inputs.module.scss';

export const Autocomplete = <T extends number | string>({
  label,
  error,
  placeholder,
  options,
  value,
  onChange,
  disabled,
  className,
  onNewOption,
  newOptionLabel,
  alwaysOfferNewOption,
  groups,
  mandatory,
  ...props
}: {
  label: string;
  error?: string;
  value: string;
  onChange(id: string): void;
  /** `onRemove` puts a delete cross on the row; the row itself still picks the option. */
  options: {
    label: React.ReactNode;
    value: T;
    disabled?: boolean;
    onRemove?(): void;
    removeLabel?: string;
    /** The `groups` entry this option is listed under; ungrouped options come first. */
    group?: string;
  }[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onNewOption?(option: string): void;
  /** Labels the "add" row; receives the typed text, which is empty when nothing was typed. */
  newOptionLabel?(typed: string): React.ReactNode;
  /** Keeps the "add" row on offer with an empty box, for pickers that create by name alone. */
  alwaysOfferNewOption?: boolean;
  /**
   * Splits the list under headings, in this order. A group with no options shows its
   * `empty` line instead of vanishing, so the list says what is missing rather than hiding it.
   */
  groups?: readonly { name: string; empty?: React.ReactNode }[];
  mandatory?: boolean;
} & (
  | {
      onItemSelect(id: null | T): void;
      selectedOptionLabel?: null | React.ReactNode;
    }
  | {
      onMultipleItemsSelect(ids: { label: React.ReactNode; value: T }[]): void;
      selectedOptions: { label: React.ReactNode; value: T }[];
    }
)) => {
  const [opened, setIsOpened] = useState(false);
  const popoutRef = useRef<HTMLDivElement>(null);
  /* The list is portalled to <body>, so a click on an option is outside this
     label — without checking the popout too, picking closed the list on
     mousedown and the option unmounted before its click landed. */
  const ref = useClickOutside<HTMLLabelElement>((event) => {
    if (popoutRef.current?.contains(event.target as Node)) return;
    setIsOpened(false);
  });
  const anchorRef = useRef<HTMLDivElement>(null);
  const [popoutStyle, setPopoutStyle] = useState<CSSProperties>({});

  /* The list is portalled to <body> and placed against the field's rect. Left in
     the flow it was clipped by whatever scrolls around it — a wizard step, a
     side panel, a modal — which is why one screen kept a copy of this component
     with its own portal instead of using it. Opens upwards when it does not fit
     below, and measures against visualViewport so a phone's keyboard does not
     push it off-screen. */
  useEffect(() => {
    if (!opened) return;
    let frame = 0;
    let last = '';
    const place = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const maxHeight = 200;
      const keyboardInset =
        Number.parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--keyboard-inset'),
        ) || 0;
      const viewportBottom = (window.visualViewport?.height ?? window.innerHeight) - keyboardInset;
      const spaceBelow = viewportBottom - rect.bottom;
      const up = spaceBelow < maxHeight && rect.top > spaceBelow;
      const next: CSSProperties = {
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        right: 'auto',
        zIndex: 10000,
        maxHeight,
        ...(up
          ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
          : { top: rect.bottom + 4 }),
      };
      const key = JSON.stringify(next);
      if (key === last) return;
      last = key;
      setPopoutStyle(next);
    };
    const track = () => {
      place();
      frame = requestAnimationFrame(track);
    };
    track();
    return () => cancelAnimationFrame(frame);
  }, [opened]);

  const isMultiple = 'onMultipleItemsSelect' in props;
  const offersNewOption =
    !!onNewOption &&
    !!(value || alwaysOfferNewOption) &&
    !options.some(({ label }) => label === value);

  const groupNames = new Set(groups?.map(({ name }) => name));
  const ungrouped = options.filter(({ group }) => !group || !groupNames.has(group));
  const hasEmptyGroup = !!groups?.some(
    ({ name, empty }) => !!empty && !options.some(({ group }) => group === name),
  );

  const renderOption = ({
    label,
    value,
    disabled: optionDisabled,
    onRemove,
    removeLabel,
  }: (typeof options)[number]) => (
    <li
      key={value}
      className={optionDisabled ? s.optionDisabled : undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        if (optionDisabled) return;
        if (isMultiple) {
          props.onMultipleItemsSelect([...props.selectedOptions, { label, value }]);
        } else {
          props.onItemSelect(value);
          setIsOpened(false);
        }
      }}
    >
      {onRemove ? (
        <>
          <span className={s.optionLabel}>{label}</span>
          <button
            type="button"
            className={s.optionRemove}
            aria-label={removeLabel ?? 'Remove'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpened(false);
              onRemove();
            }}
          >
            <BiX size={16} aria-hidden="true" />
          </button>
        </>
      ) : (
        label
      )}
    </li>
  );

  const handleRemove = (valueToRemove?: T) => {
    if (!disabled) {
      if (isMultiple) {
        props.onMultipleItemsSelect(
          props.selectedOptions.filter(({ value }) => value !== valueToRemove),
        );
      } else {
        props.onItemSelect(null);
        onChange('');
        setIsOpened(false);
      }
    }
  };

  /* Picking an option keeps focus in the box (the rows cancel mousedown), so a
     focus handler alone left the list shut on the next click or keystroke — a
     multi-entry field like the event wizard's Location went dead until it was
     blurred. Clicking and typing reopen it too. */
  const handleOpen = () => {
    if (!disabled) {
      setIsOpened(true);
    }
  };

  return (
    <label className={clsx(s.InputRoot, className)} ref={ref}>
      <div className={s.Label}>
        <span>{label}</span>
        {mandatory && <span>*</span>}
      </div>
      <div
        ref={anchorRef}
        className={clsx(
          s.InputWrapper,
          s.SelectWrapper,
          s.AutocompleteWrapper,
          disabled && s.disabled,
          error && s.error,
          opened && s.Opened,
          isMultiple && s.withIcon,
        )}
      >
        {isMultiple ? (
          <>
            {props.selectedOptions.map(({ label, value }) => (
              <div
                key={value}
                title={typeof label === 'string' ? label : ''}
                className={s.selectedValue}
                onClick={() => handleRemove(value)}
              >
                <span>{label}</span> <BiX size={16} />
              </div>
            ))}
            <input
              type="text"
              className="body-medium"
              placeholder={placeholder}
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                handleOpen();
              }}
              onFocus={handleOpen}
              onClick={handleOpen}
              onBlur={() => setIsOpened(false)}
              disabled={disabled}
            />
          </>
        ) : props.selectedOptionLabel ? (
          <div
            title={typeof props.selectedOptionLabel === 'string' ? props.selectedOptionLabel : ''}
            className={s.selectedValue}
            onClick={() => handleRemove()}
          >
            <span>{props.selectedOptionLabel}</span> <BiX size={16} />
          </div>
        ) : (
          <input
            type="text"
            className="body-medium"
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              handleOpen();
            }}
            onFocus={handleOpen}
            onClick={handleOpen}
            onBlur={() => setIsOpened(false)}
            disabled={disabled}
          />
        )}
        {isMultiple && <BiSolidChevronDown className={s.icon} />}
        {/* A single picker that holds a value shows the value, not the search, so the list
            goes with the search — even when it was opened just before the value landed.
            An empty list stays closed too: its border alone drew a stray line under the field
            while a search had nothing to offer yet. */}
        {opened &&
          !disabled &&
          (isMultiple || !props.selectedOptionLabel) &&
          (options.length > 0 || offersNewOption || hasEmptyGroup) &&
          createPortal(
            <div ref={popoutRef} className={s.Popout} style={popoutStyle} data-floating-child="">
              <ul className={s.PopoutScroll}>
                {offersNewOption && (
                  <li
                    className={s.newOption}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onNewOption?.(value);
                      // Same rule the option rows follow: a single-value picker is
                      // finished once something is picked, a multiple one is being
                      // filled and stays open for the next entry.
                      if (!isMultiple) setIsOpened(false);
                    }}
                  >
                    {newOptionLabel ? newOptionLabel(value) : <>Add &quot;{value}&quot;</>}
                  </li>
                )}
                {ungrouped.map(renderOption)}
                {groups?.map(({ name, empty }) => {
                  const grouped = options.filter((option) => option.group === name);
                  if (grouped.length === 0 && !empty) return null;
                  return (
                    <Fragment key={`group:${name}`}>
                      <li className={s.groupHeader} role="presentation">
                        {name}
                      </li>
                      {grouped.length > 0 ? (
                        grouped.map(renderOption)
                      ) : (
                        <li className={s.groupEmpty} role="presentation">
                          {empty}
                        </li>
                      )}
                    </Fragment>
                  );
                })}
              </ul>
            </div>,
            document.body,
          )}
      </div>
      {!!error && <p className={s.Error}>{error}</p>}
    </label>
  );
};
